import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import type {
  User,
  Conversation,
  ChatRequest,
  ChatResponse,
  ApiError
} from '../types';

// The token provider can be called with { force: true } to demand a fresh
// token — the AuthContext will fall back to interactive acquisition
// (popup / redirect) when a silent refresh isn't enough.
type TokenProvider = (opts?: { force?: boolean }) => Promise<string | null>;

// We attach a `_retriedFor401` flag to the outgoing request config so the
// response interceptor knows not to loop when the retry also 401s. The
// Axios type doesn't include it, so this is a small internal augmentation.
type RetriableConfig = AxiosRequestConfig & { _retriedFor401?: boolean };

class ApiService {
  private client: AxiosInstance;
  private baseURL: string;
  private tokenProvider: TokenProvider | null = null;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api';
    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      // Matches the Function App's functionTimeout (120s). Any request that
      // runs longer than this is aborted client-side rather than hanging.
      timeout: 120000,
    });

    // Request interceptor: fetch a fresh access token from MSAL before every
    // request. MSAL caches valid tokens and only does a network call when the
    // cached one is expired (or near expiry), so this is cheap on the hot
    // path and prevents the 401-on-stale-token problem from a long browser
    // session.
    this.client.interceptors.request.use(async (config) => {
      if (this.tokenProvider) {
        try {
          const token = await this.tokenProvider();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (err) {
          console.warn('Failed to acquire token for request:', err);
        }
      }
      return config;
    });

    // Response interceptor:
    //   • On 401 from any /api/* call, ask the AuthContext for a fresh
    //     token WITH the force flag (which triggers popup/redirect if
    //     silent refresh isn't sufficient), then retry the original
    //     request exactly once. Only surface the API error to the caller
    //     (and therefore to the toast) if the retry also fails.
    //   • On any other error, unwrap the API's structured `error` body
    //     when present so callers get a consistent ApiError shape.
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;
        const original = (error.config || {}) as RetriableConfig;

        if (
          status === 401 &&
          this.tokenProvider &&
          !original._retriedFor401
        ) {
          original._retriedFor401 = true;
          try {
            const freshToken = await this.tokenProvider({ force: true });
            if (freshToken) {
              original.headers = {
                ...(original.headers || {}),
                Authorization: `Bearer ${freshToken}`,
              };
              return this.client.request(original);
            }
          } catch (retryErr) {
            console.warn('Forced token acquisition failed on 401 retry:', retryErr);
          }
          // If we get here, either the token provider couldn't get a new
          // token, or the retry itself failed. Fall through to the normal
          // error propagation below.
        }

        console.error('API Error:', error);
        if (error.response?.data?.error) {
          throw error.response.data as ApiError;
        }
        throw error;
      }
    );
  }

  // Register a token provider (called by AuthContext once MSAL is ready).
  // The provider is invoked before every request so the token is always
  // fresh — MSAL handles silent refresh transparently.
  setTokenProvider(provider: TokenProvider | null) {
    this.tokenProvider = provider;
  }

  // Set authorization token (for Azure AD). Kept for back-compat with the
  // initial login flow before the provider is wired up.
  setAuthToken(token: string | null) {
    if (token) {
      this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.client.defaults.headers.common['Authorization'];
    }
  }

  // User endpoints
  async getUsers(): Promise<User[]> {
    const response = await this.client.get<User[]>('/user-list');
    return response.data;
  }

  // Conversation endpoints
  async getUserConversations(
    userId: string,
    page: number = 1,
    pageSize: number = 100
  ): Promise<Conversation[]> {
    const params = { user_id: userId, page, page_size: pageSize };
    console.log('API Request params:', params);
    const response = await this.client.get<{ conversations: Conversation[] }>('/get-user-conversations', {
      params,
    });
    console.log('API Response pageSize:', response.data);
    return response.data.conversations;
  }

  // Chat endpoint
  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    const response = await this.client.post<ChatResponse>('/chat', request);
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      await this.client.get('/health');
      return true;
    } catch {
      return false;
    }
  }
}

export const apiService = new ApiService();
export default apiService;
